import React from "react";

// ─────────────────────────────────────────────────────────────────
// STYLE SYSTEM
// ─────────────────────────────────────────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Definition / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Bracket({ x, y, h, side, color='#1F3864', w=10 }) {
  if (side === 'left')  return <path d={`M ${x+w},${y} L ${x},${y} L ${x},${y+h} L ${x+w},${y+h}`} fill="none" stroke={color} strokeWidth="2"/>;
  if (side === 'right') return <path d={`M ${x-w},${y} L ${x},${y} L ${x},${y+h} L ${x-w},${y+h}`} fill="none" stroke={color} strokeWidth="2"/>;
  return null;
}

// Compact matrix renderer
function Mat({ data, x0, y0, cW=50, cH=36, colors=null, bold=null, color='#1F3864', dividerCol=-1 }) {
  const rows = data.length, cols = data[0].length;
  const W = cols * cW, H = rows * cH;
  return (
    <g>
      <Bracket x={x0-5} y={y0-4} h={H+8} side="left"  color={color}/>
      <Bracket x={x0+W+5} y={y0-4} h={H+8} side="right" color={color}/>
      {dividerCol >= 0 && (
        <line x1={x0+dividerCol*cW} y1={y0-4} x2={x0+dividerCol*cW} y2={y0+H+4}
          stroke="#888" strokeWidth="1.5" strokeDasharray="4,3"/>
      )}
      {data.map((row, r) => row.map((val, c) => {
        const cx = x0 + c*cW + cW/2;
        const cy = y0 + r*cH + cH/2 + 5;
        const bg  = colors && colors[r]?.[c] ? colors[r][c] : null;
        const fw  = bold  && bold[r]?.[c]   ? '700' : '400';
        const fc  = bg ? '#1F3864' : '#1F3864';
        return (
          <g key={`${r}-${c}`}>
            {bg && <rect x={x0+c*cW+2} y={y0+r*cH+2} width={cW-4} height={cH-4} rx={3} fill={bg} opacity="0.65"/>}
            <text x={cx} y={cy} textAnchor="middle" fontSize="13"
              fill={fc} fontFamily="'Courier New',monospace" fontWeight={fw}>
              {val}
            </text>
          </g>
        );
      }))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Three geometric outcomes in 2D
// Unique solution, no solution, infinite solutions
// ─────────────────────────────────────────────────────────────────
function DiagramGeometric() {
  const W = 880, H = 320;
  const sc = 44;

  const cases = [
    { x: 10,  label: 'Unique Solution',    sub: 'Lines intersect at one point', color: '#27ae60', ok: 'one' },
    { x: 310, label: 'No Solution',        sub: 'Parallel lines — never meet',  color: '#c0392b', ok: 'none' },
    { x: 610, label: 'Infinite Solutions', sub: 'Same line — every point works', color: '#9b59b6', ok: 'inf' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Three Possible Outcomes — 2D Geometric Interpretation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {cases.map((cas, ci) => {
          const pw = 260, ph = 300;
          const ox = cas.x + 130, oy = 168;

          return (
            <g key={ci}>
              {/* Panel */}
              <rect x={cas.x} y={8} width={pw} height={ph} rx={6}
                fill="#fff" stroke={cas.color} strokeWidth="1.8"/>
              <text x={cas.x+pw/2} y={26} textAnchor="middle" fontSize="13"
                fill={cas.color} fontWeight="700">{cas.label}</text>
              <text x={cas.x+pw/2} y={42} textAnchor="middle" fontSize="11.5"
                fill="#555">{cas.sub}</text>

              {/* Grid */}
              {[-2,-1,0,1,2].map(v => (
                <g key={v}>
                  <line x1={cas.x+6} y1={oy+v*sc} x2={cas.x+pw-6} y2={oy+v*sc} stroke="#f0f0f0" strokeWidth="1"/>
                  <line x1={ox+v*sc} y1={50} x2={ox+v*sc} y2={ph-10} stroke="#f0f0f0" strokeWidth="1"/>
                </g>
              ))}
              <line x1={cas.x+6} y1={oy} x2={cas.x+pw-6} y2={oy} stroke="#ccc" strokeWidth="1.2"/>
              <line x1={ox} y1={50} x2={ox} y2={ph-10} stroke="#ccc" strokeWidth="1.2"/>

              {/* UNIQUE: two lines crossing */}
              {cas.ok === 'one' && (() => {
                // Line 1: y = x + 0  →  through (-2,-2) to (2,2)
                // Line 2: y = -x + 1 →  through (-2,3) to (2,-1) ... intersect at (0.5, 0.5)
                const l1x1=ox-2*sc, l1y1=oy+2*sc, l1x2=ox+2.2*sc, l1y2=oy-2.2*sc;
                const l2x1=ox-2*sc, l2y1=oy-1*sc, l2x2=ox+2.2*sc, l2y2=oy+3.2*sc;
                // intersection at x=0.5, y=0.5
                const ix=ox+0.5*sc, iy=oy-0.5*sc;
                return (
                  <g>
                    <line x1={l1x1} y1={l1y1} x2={l1x2} y2={l1y2} stroke="#2E75B6" strokeWidth="2.2"/>
                    <line x1={l2x1} y1={l2y1} x2={l2x2} y2={l2y2} stroke="#e07b00" strokeWidth="2.2"/>
                    <circle cx={ix} cy={iy} r={6} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
                    <text x={ix+10} y={iy-8} fontSize="12" fill="#27ae60" fontWeight="700">(0.5, 0.5)</text>
                    <text x={cas.x+6} y={l1y1-6} fontSize="11" fill="#2E75B6" fontWeight="700">L₁</text>
                    <text x={cas.x+6} y={l2y1+14} fontSize="11" fill="#e07b00" fontWeight="700">L₂</text>
                    <rect x={cas.x+8} y={258} width={pw-16} height={32} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
                    <text x={cas.x+pw/2} y={272} textAnchor="middle" fontSize="11.5" fill="#27ae60" fontWeight="700">rank(A)=rank([A|b])=n=2</text>
                    <text x={cas.x+pw/2} y={286} textAnchor="middle" fontSize="11" fill="#555">Unique x satisfies both equations</text>
                  </g>
                );
              })()}

              {/* NO SOLUTION: parallel lines */}
              {cas.ok === 'none' && (() => {
                const l1x1=cas.x+8, l1y1=oy+1.8*sc, l1x2=cas.x+pw-8, l1y2=oy-1.6*sc;
                const l2x1=cas.x+8, l2y1=oy+0.4*sc, l2x2=cas.x+pw-8, l2y2=oy-3.0*sc;
                return (
                  <g>
                    <line x1={l1x1} y1={l1y1} x2={l1x2} y2={l1y2} stroke="#2E75B6" strokeWidth="2.2"/>
                    <line x1={l2x1} y1={l2y1} x2={l2x2} y2={l2y2} stroke="#e07b00" strokeWidth="2.2"/>
                    {/* gap arrows showing they never meet */}
                    <line x1={ox+sc} y1={oy-0.7*sc} x2={ox+sc} y2={oy-1.7*sc}
                      stroke="#c0392b" strokeWidth="1.5" strokeDasharray="3,2"/>
                    <text x={ox+sc+6} y={oy-1.1*sc} fontSize="11" fill="#c0392b">gap</text>
                    <text x={cas.x+6} y={l1y1-4} fontSize="11" fill="#2E75B6" fontWeight="700">L₁</text>
                    <text x={cas.x+6} y={l2y1+14} fontSize="11" fill="#e07b00" fontWeight="700">L₂</text>
                    <rect x={cas.x+8} y={258} width={pw-16} height={32} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
                    <text x={cas.x+pw/2} y={272} textAnchor="middle" fontSize="11.5" fill="#c0392b" fontWeight="700">rank(A) &lt; rank([A|b])</text>
                    <text x={cas.x+pw/2} y={286} textAnchor="middle" fontSize="11" fill="#555">Inconsistent — no x satisfies both</text>
                  </g>
                );
              })()}

              {/* INFINITE: same line */}
              {cas.ok === 'inf' && (() => {
                const lx1=cas.x+8, ly1=oy+2*sc, lx2=cas.x+pw-8, ly2=oy-2*sc;
                return (
                  <g>
                    {/* Draw same line twice with offset to show overlap */}
                    <line x1={lx1} y1={ly1} x2={lx2} y2={ly2} stroke="#9b59b6" strokeWidth="5" opacity="0.25"/>
                    <line x1={lx1} y1={ly1} x2={lx2} y2={ly2} stroke="#9b59b6" strokeWidth="2.5"/>
                    {/* dots showing many solutions */}
                    {[-1.2,-0.5,0,0.5,1.2].map((t,i) => (
                      <circle key={i} cx={ox+t*sc} cy={oy-t*sc} r={4}
                        fill="#9b59b6" stroke="#fff" strokeWidth="1.5" opacity="0.8"/>
                    ))}
                    <text x={lx2-30} y={ly2-10} fontSize="11" fill="#9b59b6" fontWeight="700">L₁ = L₂</text>
                    <rect x={cas.x+8} y={258} width={pw-16} height={32} rx={3} fill="#f5eeff" stroke="#9b59b6" strokeWidth="1"/>
                    <text x={cas.x+pw/2} y={272} textAnchor="middle" fontSize="11.5" fill="#9b59b6" fontWeight="700">rank(A)=rank([A|b]) &lt; n</text>
                    <text x={cas.x+pw/2} y={286} textAnchor="middle" fontSize="11" fill="#555">Free variable — infinitely many x</text>
                  </g>
                );
              })()}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        In 2D, each equation is a line. The solution is wherever the lines land relative to each other. Rank of the coefficient matrix vs rank of the augmented matrix is the algebraic test for which case you are in — the geometry just makes it visual.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Augmented Matrix and Row Operations
// Show the full Gaussian elimination process step by step
// ─────────────────────────────────────────────────────────────────
function DiagramGaussian() {
  const W = 880, H = 370;

  // System: 2x + y - z = 8
  //          -3x - y + 2z = -11
  //         -2x + y + 2z = -3
  // Solution: x=2, y=3, z=-1

  const steps = [
    {
      label: 'Augmented Matrix [A|b]',
      note:  'Write equations as rows',
      data:  [['2','1','-1','|','8'],['-3','-1','2','|','-11'],['-2','1','2','|','-3']],
      hRows: [],
      hl:    [],
    },
    {
      label: 'R2 → R2 + (3/2)R1',
      note:  'Eliminate x from row 2',
      data:  [['2','1','-1','|','8'],['0','½','½','|','1'],['-2','1','2','|','-3']],
      hRows: [1],
      hl:    [[1,0]],
    },
    {
      label: 'R3 → R3 + R1',
      note:  'Eliminate x from row 3',
      data:  [['2','1','-1','|','8'],['0','½','½','|','1'],['0','2','1','|','5']],
      hRows: [2],
      hl:    [[2,0]],
    },
    {
      label: 'R3 → R3 − 4R2',
      note:  'Eliminate y from row 3',
      data:  [['2','1','-1','|','8'],['0','½','½','|','1'],['0','0','-1','|','1']],
      hRows: [2],
      hl:    [[2,1]],
    },
    {
      label: 'Row Echelon Form',
      note:  'Back-substitute to find x,y,z',
      data:  [['2','1','-1','|','8'],['0','½','½','|','1'],['0','0','-1','|','1']],
      hRows: [],
      hl:    [[0,0],[1,1],[2,2]],
      pivots: true,
    },
  ];

  const cW=44, cH=38, x0s=[10,186,362,538,714];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Gaussian Elimination — Full Worked Example</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* defs for arrowhead */}
        <defs>
          <marker id="ge_arr" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#888"/>
          </marker>
        </defs>

        {steps.map((st, si) => {
          const px = x0s[si];
          const pw = 160;

          return (
            <g key={si}>
              {/* Panel */}
              <rect x={px} y={8} width={pw} height={350} rx={5}
                fill="#fff" stroke={st.pivots ? '#27ae60' : '#2E75B6'} strokeWidth={st.pivots ? 2 : 1.4}/>

              {/* Step label */}
              <text x={px+pw/2} y={24} textAnchor="middle" fontSize="11"
                fill={st.pivots ? '#27ae60' : '#1F3864'} fontWeight="700">{`Step ${si+1}`}</text>
              <text x={px+pw/2} y={38} textAnchor="middle" fontSize="10.5"
                fill={st.pivots ? '#27ae60' : '#2E75B6'} fontWeight="700">{st.label}</text>
              <text x={px+pw/2} y={52} textAnchor="middle" fontSize="10"
                fill="#888">{st.note}</text>

              {/* Matrix */}
              {st.data.map((row, r) => {
                const isHRow = st.hRows.includes(r);
                return row.map((val, c) => {
                  const isPivot = st.hl.some(([pr,pc]) => pr===r && pc===c);
                  const isDivider = val === '|';
                  const cx = px + 12 + c*cW + cW/2;
                  const cy = 68 + r*cH + cH/2;
                  const bg = isPivot ? '#d0e8ff' : isHRow ? '#fff7ee' : null;
                  return (
                    <g key={`${r}-${c}`}>
                      {isDivider && (
                        <line x1={cx} y1={62} x2={cx} y2={68+3*cH+10}
                          stroke="#aaa" strokeWidth="1.2" strokeDasharray="3,2"/>
                      )}
                      {bg && !isDivider && (
                        <rect x={px+12+c*cW+2} y={68+r*cH+2} width={cW-4} height={cH-4}
                          rx={3} fill={bg} opacity="0.7"/>
                      )}
                      {!isDivider && (
                        <text x={cx} y={cy+5} textAnchor="middle" fontSize="12.5"
                          fill={isPivot ? '#1F3864' : isHRow ? '#e07b00' : '#1F3864'}
                          fontFamily="'Courier New',monospace"
                          fontWeight={isPivot ? '700' : '400'}>
                          {val}
                        </text>
                      )}
                    </g>
                  );
                });
              })}

              {/* Bracket around matrix */}
              <path d={`M ${px+20},${62} L ${px+12},${62} L ${px+12},${68+3*cH+6} L ${px+20},${68+3*cH+6}`}
                fill="none" stroke="#1F3864" strokeWidth="1.8"/>
              <path d={`M ${px+pw-20},${62} L ${px+pw-12},${62} L ${px+pw-12},${68+3*cH+6} L ${px+pw-20},${68+3*cH+6}`}
                fill="none" stroke="#1F3864" strokeWidth="1.8"/>

              {/* Pivot labels on final step */}
              {st.pivots && (
                <g>
                  {[[0,'pivot₁'],[1,'pivot₂'],[2,'pivot₃']].map(([r, lbl]) => (
                    <text key={r} x={px+12+r*cW+cW/2} y={68+r*cH-6}
                      textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">{lbl}</text>
                  ))}
                </g>
              )}

              {/* Back-substitution result box */}
              {st.pivots && (
                <g>
                  <rect x={px+8} y={210} width={pw-16} height={90} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
                  <text x={px+pw/2} y={226} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">Back-substitute:</text>
                  <text x={px+pw/2} y={244} textAnchor="middle" fontSize="11.5" fill="#1F3864" fontFamily="'Courier New',monospace">-z = 1  → z=-1</text>
                  <text x={px+pw/2} y={262} textAnchor="middle" fontSize="11.5" fill="#1F3864" fontFamily="'Courier New',monospace">½y = 1-½z → y=3</text>
                  <text x={px+pw/2} y={280} textAnchor="middle" fontSize="11.5" fill="#1F3864" fontFamily="'Courier New',monospace">2x = 8-y+z → x=2</text>
                  <rect x={px+8} y={298} width={pw-16} height={32} rx={3} fill="#27ae60" opacity="0.15" stroke="#27ae60" strokeWidth="1"/>
                  <text x={px+pw/2} y={315} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">x=2, y=3, z=−1 ✓</text>
                </g>
              )}

              {/* Arrow to next step */}
              {si < steps.length-1 && (
                <text x={px+pw+8} y={145} fontSize="18" fill="#aaa">→</text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Gaussian elimination converts the augmented matrix to row echelon form using three row operations: swap rows, scale a row, add a multiple of one row to another. Pivots (highlighted blue on the final step) mark each independent equation. Back-substitution then reads off the solution from the bottom row upward.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Solution types via rank (2×4 summary grid)
// ─────────────────────────────────────────────────────────────────
function DiagramSolutionTypes() {
  const W = 880, H = 300;

  const cases = [
    {
      x: 10, y: 10, w: 200, h: 270,
      title: 'Unique Solution',
      color: '#27ae60',
      bg: '#f0fff4',
      cond: 'rank(A) = rank([A|b]) = n',
      geo2d: 'Two lines cross at one point',
      geo3d: 'Two planes meet on a line;\nthree planes at one point',
      free: '0 free variables',
      example: '3×3 full rank system',
    },
    {
      x: 222, y: 10, w: 200, h: 270,
      title: 'No Solution',
      color: '#c0392b',
      bg: '#fff0f0',
      cond: 'rank(A) < rank([A|b])',
      geo2d: 'Parallel lines — never meet',
      geo3d: 'Contradictory planes',
      free: 'N/A — inconsistent',
      example: 'Overdetermined, conflicting',
    },
    {
      x: 434, y: 10, w: 200, h: 270,
      title: '∞ Solutions',
      color: '#9b59b6',
      bg: '#f5eeff',
      cond: 'rank(A) = rank([A|b]) < n',
      geo2d: 'Identical lines — all overlap',
      geo3d: 'Planes share a line or plane',
      free: 'n − rank free variables',
      example: 'Underdetermined system',
    },
    {
      x: 646, y: 10, w: 224, h: 270,
      title: 'Least-Squares Solution',
      color: '#e07b00',
      bg: '#fff7ee',
      cond: 'Overdetermined: m > n, no exact solution',
      geo2d: 'More lines than unknowns',
      geo3d: 'More equations than unknowns',
      free: 'Minimise ‖Ax − b‖²',
      example: 'Fitting, sensor fusion, regression',
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Solution Types at a Glance — Rank Conditions and Geometry</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {cases.map((c, i) => (
          <g key={i}>
            <rect x={c.x} y={c.y} width={c.w} height={c.h} rx={6}
              fill={c.bg} stroke={c.color} strokeWidth="2"/>

            {/* Title bar */}
            <rect x={c.x} y={c.y} width={c.w} height={32} rx={6} fill={c.color} opacity="0.15"/>
            <rect x={c.x} y={c.y+20} width={c.w} height={12} fill={c.color} opacity="0.15"/>
            <text x={c.x+c.w/2} y={c.y+20} textAnchor="middle" fontSize="13"
              fill={c.color} fontWeight="700">{c.title}</text>

            {/* Rank condition */}
            <text x={c.x+10} y={c.y+50} fontSize="11" fill="#1F3864" fontWeight="700">Rank condition:</text>
            <text x={c.x+10} y={c.y+65} fontSize="10.5" fill={c.color}
              fontFamily="'Courier New',monospace">{c.cond}</text>

            {/* 2D geometry */}
            <text x={c.x+10} y={c.y+92} fontSize="11" fill="#1F3864" fontWeight="700">2D geometry:</text>
            <text x={c.x+10} y={c.y+107} fontSize="10.5" fill="#555">{c.geo2d}</text>

            {/* 3D geometry */}
            <text x={c.x+10} y={c.y+130} fontSize="11" fill="#1F3864" fontWeight="700">3D geometry:</text>
            {c.geo3d.split('\n').map((line, li) => (
              <text key={li} x={c.x+10} y={c.y+145+li*14} fontSize="10.5" fill="#555">{line}</text>
            ))}

            {/* Free variables */}
            <text x={c.x+10} y={c.y+186} fontSize="11" fill="#1F3864" fontWeight="700">Free variables:</text>
            <text x={c.x+10} y={c.y+200} fontSize="10.5" fill={c.color} fontFamily="'Courier New',monospace">{c.free}</text>

            {/* Example */}
            <rect x={c.x+8} y={c.y+216} width={c.w-16} height={42} rx={3}
              fill="#fff" stroke={c.color} strokeWidth="1" opacity="0.8"/>
            <text x={c.x+c.w/2} y={c.y+231} textAnchor="middle" fontSize="10.5"
              fill="#888">Common in engineering:</text>
            <text x={c.x+c.w/2} y={c.y+248} textAnchor="middle" fontSize="11"
              fill={c.color} fontWeight="700">{c.example}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The rank of A vs the rank of the augmented matrix [A|b] determines which case you are in. The number of free variables equals n minus the rank when solutions exist. Least-squares handles the overdetermined case by finding the closest possible answer instead of an exact one.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Free Variables and Parametric Solutions
// Show a 2×3 system with one free variable
// ─────────────────────────────────────────────────────────────────
function DiagramFreeVariables() {
  const W = 880, H = 290;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Free Variables — Writing the General Solution Parametrically</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* System box */}
        <rect x={10} y={8} width={200} height={272} rx={6} fill="#fff" stroke="#2E75B6" strokeWidth="1.6"/>
        <text x={110} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Original System</text>
        <text x={110} y={42} textAnchor="middle" fontSize="11" fill="#555">2 equations, 3 unknowns</text>
        <text x={28} y={75}  fontSize="13" fill="#1F3864" fontFamily="'Courier New',monospace">x₁ + 2x₂ + x₃ = 4</text>
        <text x={28} y={97}  fontSize="13" fill="#1F3864" fontFamily="'Courier New',monospace">2x₁ + 4x₂ + 2x₃ = 8</text>
        <rect x={18} y={116} width={174} height={28} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
        <text x={105} y={134} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">Row 2 = 2 × Row 1</text>
        <text x={110} y={163} textAnchor="middle" fontSize="11" fill="#555">After row reduction:</text>
        <text x={28} y={183} fontSize="13" fill="#27ae60" fontFamily="'Courier New',monospace" fontWeight="700">x₁ + 2x₂ + x₃ = 4</text>
        <text x={28} y={203} fontSize="13" fill="#888" fontFamily="'Courier New',monospace">0  =  0  (gone)</text>
        <rect x={18} y={220} width={174} height={50} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={105} y={238} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">rank = 1,  n = 3</text>
        <text x={105} y={254} textAnchor="middle" fontSize="11" fill="#555">free variables = 3−1 = 2</text>
        <text x={105} y={268} textAnchor="middle" fontSize="10.5" fill="#888">x₂ and x₃ are free</text>

        {/* Arrow */}
        <text x={228} y={148} fontSize="22" fill="#aaa">→</text>

        {/* Parametric solution box */}
        <rect x={248} y={8} width={250} height={272} rx={6} fill="#fff" stroke="#9b59b6" strokeWidth="1.6"/>
        <text x={373} y={26} textAnchor="middle" fontSize="13" fill="#9b59b6" fontWeight="700">Parametric Solution</text>
        <text x={373} y={42} textAnchor="middle" fontSize="11" fill="#555">Set x₂=s, x₃=t  (free parameters)</text>
        <text x={260} y={72}  fontSize="12.5" fill="#1F3864" fontFamily="'Courier New',monospace">x₁ = 4 − 2s − t</text>
        <text x={260} y={94}  fontSize="12.5" fill="#9b59b6" fontFamily="'Courier New',monospace">x₂ = s    (free)</text>
        <text x={260} y={116} fontSize="12.5" fill="#9b59b6" fontFamily="'Courier New',monospace">x₃ = t    (free)</text>
        <line x1={258} y1={128} x2={490} y2={128} stroke="#eee" strokeWidth="1"/>
        <text x={373} y={148} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Written as vectors:</text>
        <text x={260} y={170} fontSize="12" fill="#1F3864" fontFamily="'Courier New',monospace">⎡x₁⎤   ⎡4⎤     ⎡-2⎤     ⎡-1⎤</text>
        <text x={260} y={190} fontSize="12" fill="#1F3864" fontFamily="'Courier New',monospace">⎢x₂⎥ = ⎢0⎥ + s⎢ 1⎥ + t⎢ 0⎥</text>
        <text x={260} y={210} fontSize="12" fill="#1F3864" fontFamily="'Courier New',monospace">⎣x₃⎦   ⎣0⎦     ⎣ 0⎦     ⎣ 1⎦</text>
        <rect x={258} y={224} width={232} height={46} rx={3} fill="#f5eeff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={374} y={242} textAnchor="middle" fontSize="11.5" fill="#9b59b6" fontWeight="700">particular + s·null₁ + t·null₂</text>
        <text x={374} y={258} textAnchor="middle" fontSize="11" fill="#555">null vectors span the null space of A</text>
        <text x={374} y={272} textAnchor="middle" fontSize="10.5" fill="#888">every s,t gives a valid solution</text>

        {/* Geometric interpretation */}
        <rect x={516} y={8} width={354} height={272} rx={6} fill="#fff" stroke="#27ae60" strokeWidth="1.6"/>
        <text x={693} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Geometric Meaning in R³</text>
        <text x={693} y={42} textAnchor="middle" fontSize="11" fill="#555">One equation = one plane through the origin</text>

        {/* Simple 3D-ish plane sketch */}
        {(() => {
          const px=530, py=60, pw=330, ph=140;
          // draw a parallelogram to suggest a plane
          const pts = [[px+20,py+ph-20],[px+pw-40,py+ph-40],[px+pw-10,py+30],[px+50,py+10]];
          const poly = pts.map(p=>p.join(',')).join(' ');
          return (
            <g>
              <polygon points={poly} fill="#d4f0d4" opacity="0.5" stroke="#27ae60" strokeWidth="1.5"/>
              {/* two direction vectors on the plane */}
              <line x1={px+pw/2-30} y1={py+ph/2} x2={px+pw/2+60} y2={py+ph/2-20}
                stroke="#9b59b6" strokeWidth="2"
                markerEnd="url(#fv_s)"/>
              <line x1={px+pw/2-30} y1={py+ph/2} x2={px+pw/2-70} y2={py+ph/2-40}
                stroke="#c0392b" strokeWidth="2"
                markerEnd="url(#fv_t)"/>
              <defs>
                <marker id="fv_s" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <polygon points="0 0,6 3,0 6" fill="#9b59b6"/>
                </marker>
                <marker id="fv_t" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <polygon points="0 0,6 3,0 6" fill="#c0392b"/>
                </marker>
              </defs>
              <text x={px+pw/2+66} y={py+ph/2-16} fontSize="12" fill="#9b59b6" fontWeight="700">s direction</text>
              <text x={px+pw/2-110} y={py+ph/2-42} fontSize="12" fill="#c0392b" fontWeight="700">t direction</text>
              <circle cx={px+pw/2-30} cy={py+ph/2} r={4} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
              <text x={px+pw/2-42} y={py+ph/2+16} fontSize="11" fill="#27ae60">particular</text>
            </g>
          );
        })()}

        <rect x={524} y={214} width={338} height={58} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={693} y={232} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Solution set = a plane (affine subspace)</text>
        <text x={693} y={249} textAnchor="middle" fontSize="11.5" fill="#555">particular solution + any vector in null space</text>
        <text x={693} y={265} textAnchor="middle" fontSize="11" fill="#888">Two free vars → 2D family of solutions in R³</text>
      </svg>
      <div style={S.dCaption}>
        When free variables exist, the solution is not a point but an entire subspace shifted by a particular solution. Each free variable adds one dimension to the solution family. This structure — particular + null space — is the same pattern that appears in ODE general solutions.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Least Squares: overdetermined system
// Show geometrically what least squares minimises
// ─────────────────────────────────────────────────────────────────
function DiagramLeastSquares() {
  const W = 880, H = 300;
  const sc = 44;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Least-Squares — Best Approximate Solution When No Exact Solution Exists</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: scatter plot with fit line */}
        <rect x={10} y={8} width={380} height={280} rx={6} fill="#fff" stroke="#e07b00" strokeWidth="1.6"/>
        <text x={200} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Overdetermined System — More Equations Than Unknowns</text>
        <text x={200} y={42} textAnchor="middle" fontSize="11" fill="#555">5 data points, fit a line  y = mx + c  →  5 eqs, 2 unknowns</text>

        {/* Axes */}
        <line x1={50} y1={240} x2={370} y2={240} stroke="#bbb" strokeWidth="1.3"/>
        <line x1={50} y1={240} x2={50}  y2={55}  stroke="#bbb" strokeWidth="1.3"/>
        <text x={376} y={244} fontSize="12" fill="#888">x</text>
        <text x={44}  y={52}  fontSize="12" fill="#888">y</text>

        {/* Data points: (1,2),(2,2.8),(3,4.1),(4,3.9),(5,5.1) */}
        {[[1,2],[2,2.8],[3,4.1],[4,3.9],[5,5.1]].map(([x,y], i) => {
          const px = 50 + x*52, py = 240 - y*36;
          // best fit line: y ≈ 0.77x + 1.3  =>  at xi: yi_fit = 0.77*x+1.3
          const yfit = 0.77*x + 1.3;
          const pfitY = 240 - yfit*36;
          return (
            <g key={i}>
              {/* residual line */}
              <line x1={px} y1={py} x2={px} y2={pfitY}
                stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,2"/>
              {/* data point */}
              <circle cx={px} cy={py} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
            </g>
          );
        })}

        {/* Best fit line: y=0.77x+1.3, from x=0.5 to x=5.5 */}
        <line x1={50+0.5*52} y1={240-(0.77*0.5+1.3)*36}
              x2={50+5.5*52} y2={240-(0.77*5.5+1.3)*36}
          stroke="#e07b00" strokeWidth="2.5"/>
        <text x={50+5.5*52+4} y={240-(0.77*5.5+1.3)*36+4} fontSize="12" fill="#e07b00" fontWeight="700">best fit</text>

        {/* Legend */}
        <circle cx={60} cy={262} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
        <text x={72} y={266} fontSize="11" fill="#2E75B6">data points</text>
        <line x1={140} y1={262} x2={160} y2={262} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,2"/>
        <text x={166} y={266} fontSize="11" fill="#c0392b">residuals eᵢ = yᵢ − ŷᵢ</text>
        <text x={200} y={282} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">Minimise  Σeᵢ²  =  ‖Ax − b‖²</text>

        {/* RIGHT: normal equations */}
        <rect x={406} y={8} width={464} height={280} rx={6} fill="#fff" stroke="#27ae60" strokeWidth="1.6"/>
        <text x={638} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Normal Equations — The Algebraic Solution</text>

        <text x={418} y={52} fontSize="12" fill="#1F3864" fontWeight="700">System Ax=b has no exact solution because:</text>
        <text x={418} y={70} fontSize="12" fill="#555" fontFamily="'Courier New',monospace">b is NOT in the column space of A</text>

        <line x1={414} y1={80} x2={862} y2={80} stroke="#eee" strokeWidth="1"/>

        <text x={418} y={100} fontSize="12" fill="#1F3864" fontWeight="700">Least-squares: minimise ‖Ax − b‖²</text>
        <text x={418} y={118} fontSize="12" fill="#555">Project b onto column space of A.</text>
        <text x={418} y={136} fontSize="12" fill="#555">The closest point is Ax̂ = projection of b.</text>

        <line x1={414} y1={148} x2={862} y2={148} stroke="#eee" strokeWidth="1"/>

        <text x={418} y={168} fontSize="12" fill="#1F3864" fontWeight="700">Normal equations:</text>
        <rect x={416} y={176} width={436} height={34} rx={4} fill="#EBF3FB"/>
        <text x={634} y={199} textAnchor="middle" fontSize="14" fill="#27ae60"
          fontFamily="'Courier New',monospace" fontWeight="700">AᵀAx̂ = Aᵀb</text>

        <text x={418} y={228} fontSize="12" fill="#555">AᵀA is square and symmetric — always solvable.</text>
        <text x={418} y={246} fontSize="12" fill="#555">If A has full column rank, solution is unique:</text>
        <rect x={416} y={254} width={436} height={26} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={634} y={272} textAnchor="middle" fontSize="12.5" fill="#27ae60"
          fontFamily="'Courier New',monospace" fontWeight="700">x̂ = (AᵀA)⁻¹Aᵀb</text>
      </svg>
      <div style={S.dCaption}>
        Least squares handles overdetermined systems (more equations than unknowns) by minimising the total squared error. The normal equations AᵀAx̂=Aᵀb are the standard result. In practice: numpy.linalg.lstsq() or MATLAB's backslash operator on a non-square system.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function SystemsOfLinearEquations() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Systems of Linear Equations</h1>

      <p style={S.p}>
        A system of linear equations is a set of equations that must all be satisfied simultaneously. Every equation is a constraint — geometrically a line in 2D, a plane in 3D, or a hyperplane in higher dimensions. The solution is wherever all those constraints intersect. The Matrices page covers how to set up and invert matrices. This page covers the full story of what can happen when you solve: the geometry of solutions, the mechanics of elimination, what free variables mean, and what to do when no exact solution exists.
      </p>

      <InfoBox
        title="Standard Form:  Ax = b  where A is m×n, x is n×1, b is m×1"
        items={[
          "m equations, n unknowns — m rows in A, n columns",
          "Each row of A is the coefficients of one equation",
          "b is the right-hand side — the targets each equation must hit",
          "Square system: m=n.  Overdetermined: m>n.  Underdetermined: m<n.",
        ]}
      />

      <h2 style={S.h2}>Geometric Interpretation</h2>
      <DiagramGeometric />
      <Table2 rows={[
        ["Each equation = a hyperplane", "In Rⁿ, one linear equation defines an (n−1)-dimensional flat surface\n2D: line.  3D: plane.  nD: hyperplane."],
        ["Solution = intersection",      "The solution set is where all hyperplanes meet simultaneously\nMore equations = more constraints = smaller (or empty) intersection"],
        ["Unique solution (2D)",         "Two non-parallel lines cross at exactly one point\nrank(A) = rank([A|b]) = n"],
        ["No solution (2D)",             "Parallel lines — same slope, different intercepts\nrank(A) < rank([A|b]) — b contradicts the equations"],
        ["Infinite solutions (2D)",      "Same line — equations are multiples of each other\nrank(A) = rank([A|b]) < n — free variables exist"],
        ["In 3D",                        "Two planes always meet on a line unless parallel\nThree planes: unique point, a line, a plane, or no intersection"],
      ]} />

      <h2 style={S.h2}>The Augmented Matrix</h2>
      <Table2 rows={[
        ["Augmented matrix [A|b]",   "Append b as an extra column with a divider line\nCompact way to carry both A and b through row operations"],
        ["Row operations",           "Three legal moves that preserve the solution set:\n1) Swap two rows\n2) Multiply a row by a non-zero scalar\n3) Add a multiple of one row to another"],
        ["Why operations preserve solution", "Each operation is a reversible transformation of the equation set\nThe new system has exactly the same solution as the original"],
        ["Goal of elimination",      "Reach row echelon form: zeros below each pivot\nPivots on the diagonal, zeros below — easy to back-substitute"],
        ["Reduced row echelon (RREF)","Go further: zeros above AND below pivots, each pivot = 1\nSolution can be read off directly with no back-substitution"],
      ]} />

      <h2 style={S.h2}>Gaussian Elimination — Full Worked Example</h2>
      <DiagramGaussian />
      <Table2 rows={[
        ["Step 1 — Write augmented","Form [A|b] with the divider — keep all coefficients including zeros\nNeat bookkeeping prevents errors"],
        ["Step 2 — Forward elimination","For each pivot column, zero out everything below the pivot\nMultiplier = (element below pivot) / (pivot value)"],
        ["Step 3 — Row echelon form","Upper triangular shape — all entries below diagonal are zero\nNumber of non-zero rows = rank"],
        ["Step 4 — Back substitution","Start from the last non-zero row, solve for that variable\nSubstitute upward one row at a time"],
        ["Gauss-Jordan extension",  "Continue eliminating upward too — reaches RREF\nMore operations but solution is explicit: x = value directly"],
        ["Pivot selection",         "If a zero appears in the pivot position, swap rows (partial pivoting)\nAlways swap to put the largest element in the pivot — improves accuracy"],
      ]} />

      <h2 style={S.h2}>Solution Types and Rank Conditions</h2>
      <DiagramSolutionTypes />
      <Table2 rows={[
        ["Consistent system",        "rank(A) = rank([A|b]) — b is in the column space of A\nAt least one solution exists"],
        ["Inconsistent system",      "rank(A) < rank([A|b]) — b adds a new dimension A cannot reach\nNo solution — contradiction appears as 0=nonzero in elimination"],
        ["Unique solution",          "Consistent AND rank(A) = n — no free variables\nExactly one x satisfies all equations"],
        ["Infinite solutions",       "Consistent AND rank(A) < n — free variables exist\nSolution is an affine subspace: particular + null space"],
        ["Underdetermined",          "Fewer equations than unknowns: m < n\nAt least n−m free variables if consistent — used in optimisation"],
        ["Overdetermined",           "More equations than unknowns: m > n\nTypically inconsistent — use least squares for best approximate solution"],
      ]} />

      <h2 style={S.h2}>Free Variables and the General Solution</h2>
      <DiagramFreeVariables />
      <Table2 rows={[
        ["Pivot variables",          "Variables corresponding to pivot columns — determined by the others\nOne pivot variable per independent equation"],
        ["Free variables",           "Variables in non-pivot columns — can be set to any value\nCount = n − rank(A)"],
        ["Particular solution",      "Set all free variables to zero, solve for pivot variables\nOne specific x that satisfies Ax=b"],
        ["Null space solution",      "Set one free variable to 1 (others to 0), solve — gives null vector\nAny scalar multiple of a null vector satisfies Ax=0"],
        ["General solution",         "x = x_particular + c₁·v_null₁ + c₂·v_null₂ + ...\nSame structure as ODE general solution — particular + homogeneous"],
        ["Engineering context",      "Underdetermined structures (mechanisms) have free motions\nFree variables = degrees of freedom of unconstrained motion"],
      ]} />

      <h2 style={S.h2}>Least-Squares for Overdetermined Systems</h2>
      <DiagramLeastSquares />
      <Table2 rows={[
        ["When to use it",           "m > n and the system is inconsistent — no exact solution exists\nTypical when fitting models to more data points than parameters"],
        ["What it minimises",        "‖Ax − b‖² = sum of squared residuals (errors)\nClosest possible solution in the column space of A"],
        ["Normal equations",         "AᵀAx̂ = Aᵀb  →  x̂ = (AᵀA)⁻¹Aᵀb\nAᵀA is always square and symmetric — solvable by standard methods"],
        ["Pseudo-inverse",           "x̂ = A⁺b  where A⁺ = (AᵀA)⁻¹Aᵀ is the Moore-Penrose pseudo-inverse\nHandles rank-deficient cases that the normal equations cannot"],
        ["In practice",              "numpy.linalg.lstsq(A, b)  or  MATLAB: A\\b on non-square A\nNever explicitly form (AᵀA)⁻¹ — use QR decomposition internally"],
        ["Engineering use",          "Sensor calibration, curve fitting, state estimation, photogrammetry\nAny time you have more measurements than model parameters"],
      ]} />

      <InfoBox
        title="Key Takeaway — Decision Tree for Any Linear System"
        items={[
          "First: compute rank(A) and rank([A|b]).  If they differ → no solution (inconsistent).",
          "If consistent: does rank(A) = n?  Yes → unique solution.  No → infinite solutions.",
          "Count free variables: n − rank(A).  Each one adds a dimension to the solution family.",
          "If overdetermined (m > n) and inconsistent: use least squares — AᵀAx̂ = Aᵀb.",
          "In code: numpy.linalg.solve() for square systems, numpy.linalg.lstsq() for everything else.",
        ]}
      />
    </div>
  );
}